import React, { createContext, useContext, useState, useEffect, useRef, useCallback, ReactNode } from 'react';
import {
  User,
  LoginRequest,
  LoginResponse,
  CompanyProfileResponse,
  MentorProfileResponse,
  MenteeProfileResponse,
} from '../types';
import { apiService } from '../services/api';
import {
  queryClient,
  mentorProfileQueryKeys,
  menteeProfileQueryKeys,
  companyProfileQueryKeys,
} from '../lib/queryClient';
import { isSubscriptionUiActive } from '../utils/subscriptionUiActive';

export type LoginAs = 'Mentee' | 'Mentor' | 'Company' | 'Admin';

interface AuthContextType {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  /** Rol bazlı giriş (şirket/mentee/mentor ID çakışmalarını önler). */
  login: (email: string, password: string, as: LoginAs) => Promise<void>;
  register: (userData: any) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  refreshAuthToken: () => Promise<boolean>;
  /** Ödeme / dev fake sonrası JWT + profil abonelik alanlarını günceller (Company dahil). */
  syncAuthAfterSubscription: () => Promise<boolean>;
  isLoading: boolean;
  isLoggingOut: boolean;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const lastProfileSyncKey = useRef<string | null>(null);

  useEffect(() => {
    const initializeAuth = async () => {
      const storedToken = localStorage.getItem('token');
      const storedRefreshToken = localStorage.getItem('refreshToken');
      const storedUser = localStorage.getItem('user');


      if (storedToken && storedUser) {
        setToken(storedToken);
        setUser(JSON.parse(storedUser));
        if (storedRefreshToken) {
          setRefreshToken(storedRefreshToken);
        }
        setIsLoading(false);
        return;
      }
      
      // Token yok ama refresh token varsa, refresh token ile yeni token al
      if (storedRefreshToken) {
        setRefreshToken(storedRefreshToken);
        try {
          const response = await apiService.refreshToken(storedRefreshToken);
          if (response.success && response.data) {
            const newToken = response.data.token;
            const newRefreshToken = response.data.refreshToken;
            
            setToken(newToken);
            localStorage.setItem('token', newToken);
            
            if (newRefreshToken) {
              setRefreshToken(newRefreshToken);
              localStorage.setItem('refreshToken', newRefreshToken);
            }
            
            // User bilgilerini güncelle
      const user: User = {
        id: response.data.userId,
        email: response.data.email,
        name: response.data.fullName,
        fullName: response.data.fullName,
        role: response.data.role as 'Admin' | 'Mentor' | 'Mentee' | 'Company',
        profileImage: response.data.profileImage ? response.data.profileImage.substring(0, 1000) : undefined, // Resmi kısalt
        subscriptionType: response.data.subscriptionType as 'Free' | 'Monthly' | 'Yearly',
        isSubscriptionActive: response.data.isSubscriptionActive,
        subscriptionEndDate: response.data.subscriptionEndDate
      };
            
            setUser(user);
            localStorage.setItem('user', JSON.stringify(user));
          }
        } catch (error) {
          console.error('Auto refresh failed:', error);
          // Refresh başarısız, temizle
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('refreshToken');
        }
      }
      
      setIsLoading(false);
    };

    initializeAuth();
  }, []);

  useEffect(() => {
    if (!user) {
      lastProfileSyncKey.current = null;
    }
  }, [user]);

  // Giriş sonrası e-posta/ad localStorage ile sunucu farklıysa düzelt (tek istek / kullanıcı kimliği)
  useEffect(() => {
    if (isLoading || !user) return;
    if (user.role !== 'Mentor' && user.role !== 'Mentee' && user.role !== 'Company') return;
    const key = `${user.id}-${user.role}`;
    if (lastProfileSyncKey.current === key) return;
    lastProfileSyncKey.current = key;
    void (async () => {
      try {
        let response;
        if (user.role === 'Mentor') {
          response = await apiService.getMentorProfile();
        } else if (user.role === 'Mentee') {
          response = await apiService.getMenteeProfile();
        } else {
          response = await apiService.getCompanyProfile();
        }
        if (!response.success || !response.data) return;
        const d = response.data;
        setUser((prev) => {
          if (!prev || prev.id !== user.id || prev.role !== user.role) return prev;
          if (user.role === 'Company') {
            const c = d as CompanyProfileResponse;
            const next: User = {
              ...prev,
              email: c.email,
              name: c.companyName,
              fullName: c.companyName,
              subscriptionType: c.subscriptionType as User['subscriptionType'],
              isSubscriptionActive: c.isSubscriptionActive,
              subscriptionEndDate: c.subscriptionEndDate
            };
            localStorage.setItem('user', JSON.stringify(next));
            return next;
          }
          const m = d as MentorProfileResponse | MenteeProfileResponse;
          const full = `${m.firstName ?? ''} ${m.lastName ?? ''}`.trim();
          const next: User = {
            ...prev,
            email: m.email,
            name: full || prev.name,
            fullName: full || prev.fullName,
            firstName: m.firstName,
            lastName: m.lastName,
            profileImage: m.profileImage,
            subscriptionType: m.subscriptionType as User['subscriptionType'],
            isSubscriptionActive: m.isSubscriptionActive,
            subscriptionEndDate: m.subscriptionEndDate
          };
          localStorage.setItem('user', JSON.stringify(next));
          return next;
        });
      } catch {
        lastProfileSyncKey.current = null;
      }
    })();
  }, [isLoading, user?.id, user?.role]);

  // Başka sekmede çıkış/giriş: localStorage değişince bu sekmede token ≠ state kalmasın (profil karışmasını önler)
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== 'token' && e.key !== 'user' && e.key !== 'refreshToken') return;
      if (e.storageArea !== localStorage) return;
      window.location.reload();
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const login = async (email: string, password: string, as: LoginAs) => {
    try {
      setIsLoading(true);
      const credentials: LoginRequest = { email, password };
      queryClient.clear();
      const response: LoginResponse = await apiService.login(credentials, as);
      
      // User objesini oluştur
      const user: User = {
        id: response.userId,
        email: response.email,
        name: response.fullName,
        fullName: response.fullName,
        role: response.role as 'Admin' | 'Mentor' | 'Mentee' | 'Company',
        profileImage: response.profileImage, // Resmi localStorage'da saklama, sadece state'te tut
        subscriptionType: response.subscriptionType as 'Free' | 'Monthly' | 'Yearly',
        isSubscriptionActive: response.isSubscriptionActive,
        subscriptionEndDate: response.subscriptionEndDate
      };
      
      setToken(response.token);
      setUser(user);
      
      if (response.refreshToken) {
        setRefreshToken(response.refreshToken);
        localStorage.setItem('refreshToken', response.refreshToken);
      }
      
      localStorage.setItem('token', response.token);
      localStorage.setItem('user', JSON.stringify(user));
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (userData: any) => {
    try {
      setIsLoading(true);
      // Register işlemi için API çağrısı yapılacak
      console.log('Register user data:', userData);
      // Şimdilik sadece console'a yazdırıyoruz
    } catch (error) {
      console.error('Register error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    setIsLoggingOut(true);
    try {
      if (refreshToken) {
        try {
          await apiService.revokeToken(refreshToken);
        } catch (error) {
          console.error('Token revoke error:', error);
        }
      }
      setToken(null);
      setUser(null);
      setRefreshToken(null);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('refreshToken');
      queryClient.clear();
    } finally {
      setIsLoggingOut(false);
    }
  };

  const refreshUser = async () => {
    const raw = localStorage.getItem('user');
    if (!raw) return;
    const current = JSON.parse(raw) as User;

    try {
      let response;
      if (current.role === 'Mentor') {
        response = await apiService.getMentorProfile();
      } else if (current.role === 'Mentee') {
        response = await apiService.getMenteeProfile();
      } else if (current.role === 'Company') {
        response = await apiService.getCompanyProfile();
      } else {
        return;
      }

      if (response.success && response.data) {
        const d = response.data;
        let updatedUser: User;
        if (current.role === 'Company') {
          const c = d as CompanyProfileResponse;
          updatedUser = {
            ...current,
            email: c.email,
            name: c.companyName,
            fullName: c.companyName,
            subscriptionType: c.subscriptionType as User['subscriptionType'],
            isSubscriptionActive: c.isSubscriptionActive,
            subscriptionEndDate: c.subscriptionEndDate
          };
        } else {
          const m = d as MentorProfileResponse | MenteeProfileResponse;
          const full = `${m.firstName ?? ''} ${m.lastName ?? ''}`.trim();
          updatedUser = {
            ...current,
            email: m.email,
            name: full || current.name,
            fullName: full || current.fullName,
            profileImage: m.profileImage,
            firstName: m.firstName,
            lastName: m.lastName,
            subscriptionType: m.subscriptionType as User['subscriptionType'],
            isSubscriptionActive: m.isSubscriptionActive,
            subscriptionEndDate: m.subscriptionEndDate
          };
        }

        setUser(updatedUser);
        localStorage.setItem('user', JSON.stringify(updatedUser));
      }
    } catch (error) {
      console.error('User refresh error:', error);
    }
  };

  const refreshAuthToken = async (): Promise<boolean> => {
    const effectiveRefreshToken = refreshToken || localStorage.getItem('refreshToken');
    if (!effectiveRefreshToken) {
      return false;
    }

    try {
      const response = await apiService.refreshToken(effectiveRefreshToken);
      
      if (response.success && response.data) {
        const newToken = response.data.token;
        const newRefreshToken = response.data.refreshToken;
        
        setToken(newToken);
        localStorage.setItem('token', newToken);
        
        if (newRefreshToken) {
          setRefreshToken(newRefreshToken);
          localStorage.setItem('refreshToken', newRefreshToken);
        }
        
        // User bilgilerini güncelle (subscription bilgileri dahil)
        const updatedUser: User = {
          id: response.data.userId,
          email: response.data.email,
          name: response.data.fullName,
          fullName: response.data.fullName,
          role: response.data.role as 'Admin' | 'Mentor' | 'Mentee' | 'Company',
          profileImage: response.data.profileImage ? response.data.profileImage.substring(0, 1000) : undefined,
          subscriptionType: response.data.subscriptionType as 'Free' | 'Monthly' | 'Yearly',
          isSubscriptionActive: response.data.isSubscriptionActive,
          subscriptionEndDate: response.data.subscriptionEndDate
        };
        
        setUser(updatedUser);
        localStorage.setItem('user', JSON.stringify(updatedUser));
        
        return true;
      }
    } catch (error) {
      console.error('Token refresh error:', error);
      // Token yenilenemedi, logout yap
      await logout();
    }
    
    return false;
  };

  const refreshUserRef = useRef(refreshUser);
  const refreshAuthTokenRef = useRef(refreshAuthToken);
  refreshUserRef.current = refreshUser;
  refreshAuthTokenRef.current = refreshAuthToken;

  const syncAuthAfterSubscription = useCallback(async (): Promise<boolean> => {
    try {
      await refreshAuthTokenRef.current();
      await refreshUserRef.current();
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: mentorProfileQueryKeys.profile }),
        queryClient.invalidateQueries({ queryKey: mentorProfileQueryKeys.subscription }),
        queryClient.invalidateQueries({ queryKey: mentorProfileQueryKeys.subscriptionHistory }),
        queryClient.invalidateQueries({ queryKey: menteeProfileQueryKeys.profile }),
        queryClient.invalidateQueries({ queryKey: menteeProfileQueryKeys.subscription }),
        queryClient.invalidateQueries({ queryKey: menteeProfileQueryKeys.subscriptionHistory }),
        queryClient.invalidateQueries({ queryKey: companyProfileQueryKeys.profile }),
        queryClient.invalidateQueries({ queryKey: companyProfileQueryKeys.subscription }),
        queryClient.invalidateQueries({ queryKey: companyProfileQueryKeys.subscriptionHistory }),
      ]);
    } catch (e) {
      console.error('syncAuthAfterSubscription:', e);
    }

    const raw = localStorage.getItem('user');
    if (!raw) return false;
    const u = JSON.parse(raw) as User;
    return isSubscriptionUiActive(u);
  }, []);

  const isAuthenticated = !!user && !!token;
  

  const value: AuthContextType = {
    user,
    token,
    refreshToken,
    login,
    register,
    logout,
    refreshUser,
    refreshAuthToken,
    syncAuthAfterSubscription,
    isLoading,
    isLoggingOut,
    isAuthenticated,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
